// ─────────────────────────────────────────────────────────────────────────────
// LabThree — Design Tokens (D2 Evoluto)
// Porting da /design_preview/tokens.jsx + d2-shared.jsx.
// Usato dai componenti UI in /src/ui/components.js.
// ─────────────────────────────────────────────────────────────────────────────

export const LT = {
  // ── Brand core (da brand identity ufficiale) ───────────────────────────────
  blu:    '#2563EB',
  verde:  '#22C55E',
  viola:  '#7C5AED',
  navy:   '#0F172A',

  // ── Semantici estesi ───────────────────────────────────────────────────────
  rosso:   '#DC2626', // NC / critica
  arancio: '#F59E0B', // warning / scadenza vicina
  ciano:   '#06B6D4', // info
  grigio:  '#64748B',

  // ── Scala neutri ───────────────────────────────────────────────────────────
  n0:   '#FFFFFF',
  nBg:  '#F7F8FC', // sfondo app (leggero cool)
  n50:  '#F8FAFC',
  n100: '#F1F5F9',
  n150: '#E8ECF2',
  n200: '#E2E8F0',
  n300: '#CBD5E1',
  n400: '#94A3B8',
  n500: '#64748B',
  n600: '#475569',
  n700: '#334155',
  n800: '#1E293B',
  n900: '#0F172A',

  // ── Accenti soft (fill) ────────────────────────────────────────────────────
  bluSoft:    '#EFF6FF',
  verdeSoft:  '#ECFDF5',
  violaSoft:  '#F3EFFE',
  rossoSoft:  '#FEF2F2',
  arancioSoft:'#FFFBEB',
  cianoSoft:  '#ECFEFF',

  // ── Gradients ──────────────────────────────────────────────────────────────
  grad:     'linear-gradient(135deg, #2563EB 0%, #22C55E 100%)',
  gradSoft: 'linear-gradient(135deg, rgba(37,99,235,.08) 0%, rgba(34,197,94,.08) 100%)',

  // ── Tipografia ─────────────────────────────────────────────────────────────
  sans: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
  mono: '"JetBrains Mono", "SF Mono", ui-monospace, monospace',

  // ── Raggi ──────────────────────────────────────────────────────────────────
  r: { sm: 6, md: 10, lg: 14, xl: 20, full: 999 },

  // ── Ombre ──────────────────────────────────────────────────────────────────
  sh1: '0 1px 2px rgba(15,23,42,.04)',
  sh2: '0 2px 8px rgba(15,23,42,.06), 0 1px 2px rgba(15,23,42,.03)',
  sh3: '0 10px 30px rgba(15,23,42,.08), 0 2px 6px rgba(15,23,42,.04)',
  shBlu: '0 8px 24px rgba(37,99,235,.18)',

  // ── Categorie HACCP (ambiti attività) ──────────────────────────────────────
  // La mappa è pensata per essere estesa con nuovi ambiti senza rompere il default.
  cat: {
    temperature:   { c: '#2563EB', bg: '#EFF6FF', icon: 'thermo',   label: 'Temperature' },
    pulizie:       { c: '#06B6D4', bg: '#ECFEFF', icon: 'sparkles', label: 'Pulizie' },
    manutenzioni:  { c: '#F59E0B', bg: '#FFFBEB', icon: 'wrench',   label: 'Manutenzioni' },
    formazione:    { c: '#7C5AED', bg: '#F3EFFE', icon: 'book',     label: 'Formazione' },
    produzione:    { c: '#22C55E', bg: '#ECFDF5', icon: 'chefHat',  label: 'Produzione' },
    tracciabilita: { c: '#10B981', bg: '#ECFDF5', icon: 'qr',       label: 'Tracciabilità' },
    bolle:         { c: '#EC4899', bg: '#FDF2F8', icon: 'receipt',  label: 'Bolle e fatture' },
  },
};

// Fallback di categoria per ambiti non mappati.
export const CAT_DEFAULT = { c: LT.grigio, bg: LT.n100, icon: 'list', label: 'Generico' };

// Normalizza una stringa per matching fuzzy: minuscolo, toglie accenti e tutto
// ciò che non è alfanumerico. "Bollatura / Marchiatura" → "bollaturamarchiatura".
function _norm(s) {
  return String(s || '')
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]/g, '');
}

// Regole pattern-based sugli ambiti HACCP comuni.
// Vengono provate in ordine sulla descrizione normalizzata: la prima regola
// che ha un `match` contenuto nella stringa vince. Estendere qui per altri ambiti.
export const CAT_RULES = [
  // Le 7 canoniche (prima, così vincono su pattern più generici)
  { match: ['temperatur', 'termometr'],           entry: { c: '#2563EB', bg: '#EFF6FF', icon: 'thermo'   } },
  { match: ['puliz', 'sanific', 'detersi'],       entry: { c: '#06B6D4', bg: '#ECFEFF', icon: 'sparkles' } },
  { match: ['manutenz', 'riparaz'],               entry: { c: '#F59E0B', bg: '#FFFBEB', icon: 'wrench'   } },
  { match: ['formazion', 'corso', 'addestram'],   entry: { c: '#7C5AED', bg: '#F3EFFE', icon: 'book'     } },
  { match: ['produzion', 'cucin', 'preparaz'],    entry: { c: '#22C55E', bg: '#ECFDF5', icon: 'chefHat'  } },
  { match: ['tracciabil', 'rintracc', 'lotto'],   entry: { c: '#10B981', bg: '#ECFDF5', icon: 'qr'       } },
  { match: ['bolle', 'fatture', 'ddt'],           entry: { c: '#EC4899', bg: '#FDF2F8', icon: 'receipt'  } },

  // Ambiti HACCP aggiuntivi più diffusi
  { match: ['allergen'],                          entry: { c: '#DC2626', bg: '#FEF2F2', icon: 'alert'    } },
  { match: ['infestanti', 'pest', 'derattizz', 'disinfest'], entry: { c: '#F59E0B', bg: '#FFFBEB', icon: 'bug' } },
  { match: ['analisi', 'campionament', 'tampon'], entry: { c: '#06B6D4', bg: '#ECFEFF', icon: 'flask'    } },
  // Approvvigionamento IDRICO / acqua potabile → deve stare PRIMA di "approvvigion" generico
  { match: ['idric', 'acqua', 'potab'],           entry: { c: '#06B6D4', bg: '#ECFEFF', icon: 'drop'     } },
  { match: ['approvvigion', 'accettaz', 'ricevim'], entry: { c: '#2563EB', bg: '#EFF6FF', icon: 'truck'  } },
  { match: ['bollatur', 'marchiatur', 'etichett'], entry: { c: '#7C5AED', bg: '#F3EFFE', icon: 'stamp'   } },
  { match: ['fooddefenc', 'fooddefense', 'difesaaliment', 'sabotag'], entry: { c: '#DC2626', bg: '#FEF2F2', icon: 'shield' } },
  { match: ['fornitor'],                          entry: { c: '#F59E0B', bg: '#FFFBEB', icon: 'factory'  } },
  { match: ['rifiuti', 'rsu', 'smaltim', 'scart'], entry: { c: '#22C55E', bg: '#ECFDF5', icon: 'trash'   } },
  { match: ['igien', 'personale'],                entry: { c: '#06B6D4', bg: '#ECFEFF', icon: 'handWash' } },
  { match: ['scadenz'],                           entry: { c: '#F59E0B', bg: '#FFFBEB', icon: 'calendar' } },
  { match: ['trasport', 'consegn', 'spediz'],     entry: { c: '#2563EB', bg: '#EFF6FF', icon: 'truck'    } },
  { match: ['ccp', 'autocontrollo', 'haccpplan'], entry: { c: '#DC2626', bg: '#FEF2F2', icon: 'shield'   } },
  { match: ['conservaz', 'stoccagg', 'magazzin'], entry: { c: '#2563EB', bg: '#EFF6FF', icon: 'package'  } },
  { match: ['abbattim', 'abbattit', 'raffredd'],  entry: { c: '#06B6D4', bg: '#ECFEFF', icon: 'thermo'   } },
  { match: ['imballagg', 'confez'],               entry: { c: '#F59E0B', bg: '#FFFBEB', icon: 'package'  } },
  { match: ['rintracc', 'traccia'],               entry: { c: '#10B981', bg: '#ECFDF5', icon: 'qr'       } },
];

export function catFor(chiave) {
  const raw = chiave ? String(chiave).trim() : '';
  if (!raw) return { ...CAT_DEFAULT };
  const k = _norm(raw);
  if (!k) return { ...CAT_DEFAULT, label: raw };
  // exact legacy key (compatibilità con LT.cat)
  if (LT.cat[k]) return { ...LT.cat[k], label: raw };
  // pattern fuzzy
  for (const rule of CAT_RULES) {
    for (const m of rule.match) {
      if (k.includes(m)) return { ...rule.entry, label: raw };
    }
  }
  return { ...CAT_DEFAULT, label: raw };
}

export default LT;
